import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";

// Components
import Text from "new_components/atoms/Text";
import ImageButton from "../ImageButton";
import IconWithBackground from "../IconWithBackground";
import ProgressDial from "new_components/molecules/ProgressDial";

// Constants
import colors from "styles/colors";
import typography from "styles/typography";
import { ICON_SET } from "new_components/atoms/Icon/IconSet";

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 8px;
  padding-bottom: 8px;
  width: ${({ chatStarted }) => (chatStarted ? "100%" : "98%")};
  overflow-x: auto;
  overflow-y: hidden;
  scroll-behavior: smooth;
  position: relative;
  margin-right: -8px;
  padding-right: 8px;

  /* Hide scrollbar by default but show it when scrolling is possible */
  scrollbar-width: thin; /* For Firefox */
  scrollbar-color: ${colors.primary[300]} ${colors.background.lightBlue}; /* For Firefox */

  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${colors.primary[300]};
    border-radius: 4px;
  }

  &::-webkit-scrollbar-track {
    background-color: ${colors.background.lightBlue};
  }
`;

const CardsContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  flex-wrap: nowrap;
  padding-bottom: 8px; /* Add padding to ensure shadow is visible */
  max-width: 225px;

`;

const Label = styled(Text)`
  color: ${colors.text.gray};
  margin-bottom: 4px;
`;

const Card = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px;
  border-radius: 8px;
  background-color: ${colors.background.lightBlue};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  min-width: 150px;
  flex-shrink: 0; /* Prevent cards from shrinking when scrolling horizontally */
  opacity: ${(props) => (props.uploadPending ? 0.5 : 1)};
`;

const CardColumn = styled.div`
  display: flex;
  gap: 8px;
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const DataPoints = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-left: 16px;
`;

// Component
const DataSourceCard = ({
  dataSourceConfig = [],
  selectedCards = [],
  uploadPending,
  chatStarted = false,
  fileClearCb,
  feedbackLength,
  handleRemoveAllFactors,
  handleRemoveAllOutcomes,
  handleRemoveAllCustomQuestions
}) => {
  const [selectedCardsWithCallbacks, setSelectedCardsWithCallbacks] = useState([]);
  const [showLoadingSpinner, setShowLoadingSpinner] = useState(false);

  useEffect(() => {
    const updatedCards = selectedCards?.map((selectedCard) => {
      const dataSourceConfigItem = dataSourceConfig.find(
        (configItem) => configItem.value === selectedCard.type
      );

      return {
        ...selectedCard,
        removeCb:
          dataSourceConfigItem?.removeCb ||
          (() => console.warn("Callback not provided")),
      };
    });

    setSelectedCardsWithCallbacks(updatedCards);

    // Handle loading spinner logic
    if (uploadPending) {
      const mostRecentFileCard = selectedCards
        .filter((card) => card.type === "file")
        .pop();
      if (mostRecentFileCard) {
        setShowLoadingSpinner(true);
      }
    } else {
      setShowLoadingSpinner(false);
    }
  }, [selectedCards, dataSourceConfig, uploadPending]);

  const getIconType = (type) => {
    switch (type) {
      case "pulse":
        return ICON_SET.aiIcon;
      case "culture":
        return ICON_SET.layers;
      case "factor":
        return ICON_SET.layers;
      case "outcome":
        return ICON_SET.userIcon;
      case "comment":
        return ICON_SET.chat;
      case "file":
        return ICON_SET.file;
      default:
        return ICON_SET.data;
    }
  };

  // If the title is greater than 20 characters, truncate it and add ellipsis
  const truncateTitle = (title, type, count) => {

    // if its a custom question, the title should be, "Custom Question (3)"
    // 3 is the number of custom questions selected
    if (type === "comment") {
      return `Custom Question (${count})`;
    }

    return title?.length > 35 ? `${title?.substring(0, 35)}...` : title;
  }

  // Group cards by type and check if all options are selected
  const getGroupedCards = () => {
    const groupedCards = {};
    const allOptions = {};

    // First, get all available options for each type from dataSourceConfig
    dataSourceConfig.forEach((config) => {
      if (config.options) {
        allOptions[config.value] = config.options.map(opt => opt.id || opt.value);
      }
    });

    // Group selected cards by type
    selectedCardsWithCallbacks.forEach((card) => {
      if (!groupedCards[card.type]) {
        groupedCards[card.type] = [];
      }
      groupedCards[card.type].push(card);
    });

    // Process each group
    const processedCards = [];
    Object.entries(groupedCards).forEach(([type, cards]) => {
      if (type === "file" || type === "pulse" || type === "comment") {
      // For file, pulse, and comment types, show individual cards
        processedCards.push(...cards);
      } else if (allOptions[type] && (type !== "file" && type !== "pulse" && type !== "comment")) {
        // For other types (outcomes, factors), check if all options are selected
        const selectedIds = cards.map(card => card.id);
        const allOptionsSelected = allOptions[type].length === selectedIds.length;

        if (allOptionsSelected) {
          // If all options are selected, show a single "All {type} selected" card
          processedCards.push({
            type,
            id: `all-${type}`,
            title: `All ${type === "culture" ? "engagement" : type} factors selected`,
            // Call the removeAllCb for the type,
            // each card should have a removeAllCb
            // you only need to use 1 removeAllCb
            removeCb: () => {
              if (type === "outcome") {
                handleRemoveAllOutcomes();
              } else {
                handleRemoveAllFactors();
              }
            }
          });
        } else {
          // If not all options are selected, show individual cards
          processedCards.push(...cards);
        }
      }
    });

    return processedCards;
  };

  const processedCards = getGroupedCards();

  if (processedCards.length === 0) {
    return null;
  }

  return (
    <Container chatStarted={chatStarted}>
      <CardsContainer>
        {processedCards.map((selectedCard, index) => {
          const { type, id, removeCb, title, count } = selectedCard;
          const iconType = getIconType(type);
          let capitalizedType =
            (type?.length > 0 && type.charAt(0).toUpperCase() + type.slice(1)) ||
            type;

          // Special case handling for specific types
          if (capitalizedType === "Factor") {
            capitalizedType = "Culture";
          } else if (capitalizedType === "Comment") {
            capitalizedType = "Custom Question";
          }

          let showSpinner = false;
          if (
            showLoadingSpinner &&
            type === "file" &&
            selectedCards[selectedCards.length - 1].id === id
          ) {
            showSpinner = true;
          }

          return (
            <Card key={index} uploadPending={showSpinner && uploadPending}>
              <CardColumn>
                {showSpinner && uploadPending && (
                  <ProgressDial size="30px" infinite />
                )}
                <TextContainer>
                  <Text typographyStyle={typography.smallText.regular}>
                    {truncateTitle(title, type, count)}
                  </Text>
                </TextContainer>
              </CardColumn>
              {!chatStarted && (
                <ImageButton
                  textStyle
                  icon={ICON_SET.clear}
                  onClick={() => {
                    if (type === "file" || type === "pulse") {
                      if (type === "file" && !uploadPending) {
                        fileClearCb(id);
                      } else {
                        removeCb(id);
                      }
                    } else if (type === "comment") {
                      // For custom questions, pass the entire card object
                      handleRemoveAllCustomQuestions();
                    } else {
                      removeCb(selectedCard);
                    }
                  }}
                />
              )}
            </Card>
          );
        })}
      </CardsContainer>
    </Container>
  );
};

DataSourceCard.propTypes = {
  dataSourceConfig: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      callback: PropTypes.func,
    })
  ),
  selectedCards: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string.isRequired,
      id: PropTypes.string.isRequired,
    })
  ),
  uploadPending: PropTypes.bool,
  chatStarted: PropTypes.bool,
};

export default DataSourceCard;